import React, { useCallback, useId, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import {
    Alert,
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    TextField,
    Typography,
} from '@mui/material';
import { parseTsv } from '../watchlist_table/spreadsheet/clipboardTsv';
import { isMacPlatform } from '../watchlist_table/spreadsheet/keyboardCommands';

// Pasting a sheet copied from Excel to import it into the Watchlist or the
// Actual Portfolio: the page-wide paste listener and the Paste from Excel
// dialog. What counts as a sheet, and the import it goes to, belong to the
// owner (see watchlist_table/pastedSheet.js, WatchlistTable and PortfolioUploader).

export const PASTE_SHORTCUT_LABEL = isMacPlatform() ? '⌘V' : 'Ctrl+V';
// Phones and tablets paste with a long press, not a shortcut.
const TOUCH_PRIMARY = typeof window !== 'undefined'
    && typeof window.matchMedia === 'function'
    && window.matchMedia('(pointer: coarse)').matches;
export const PASTE_ACTION = TOUCH_PRIMARY ? 'paste it' : `press ${PASTE_SHORTCUT_LABEL}`;
// Header names both imports recognise (see watchlist_table/pastedSheet.js).
export const IDENTIFIER_EXAMPLES = 'Ticker, ISIN, CUSIP or SEDOL';
// What a sheet needs, by the import it goes to.
export const NOT_A_SHEET_MESSAGES = {
    watchlist: 'Nothing was imported. The first row must be a header row naming '
        + `an identifier column, such as ${IDENTIFIER_EXAMPLES}, with your securities below it.`,
    portfolio: 'Nothing was imported. The first row must be a header row naming '
        + `an identifier column, such as ${IDENTIFIER_EXAMPLES}, and a Quantity column, `
        + 'with your holdings below it.',
};

// Text fields, dialogs and menus keep their own pastes. A MUI dialog's focus
// starts on its container, outside role="dialog", hence its modal root.
const OWN_PASTE_SELECTOR = [
    'input',
    'textarea',
    'select',
    '[contenteditable]:not([contenteditable="false"])',
    '[role="dialog"]',
    '[role="alertdialog"]',
    'dialog',
    '[aria-modal="true"]',
    '.MuiModal-root',
].join(', ');

function ownsPaste(target) {
    return typeof target?.closest === 'function' && Boolean(target.closest(OWN_PASTE_SELECTOR));
}

/**
 * A ref callback for a page's root element. While the element is mounted and
 * `enabled`, a text paste anywhere on the page outside text fields and
 * dialogs goes to onPaste(text); both are read when the paste happens. The
 * grid's own Mod+V pastes into a hidden text field, so it is not seen here.
 */
export function usePagePaste({ enabled, onPaste }) {
    const latestRef = useRef(null);
    latestRef.current = { enabled, onPaste };
    const detachRef = useRef(null);
    return useCallback((node) => {
        detachRef.current?.();
        detachRef.current = null;
        if (!node) return;
        const takes = (event) => (
            latestRef.current.enabled && !event.defaultPrevented && !ownsPaste(event.target)
        );
        // Safari offers Paste outside text fields only when beforepaste is cancelled.
        const handleBeforePaste = (event) => {
            if (takes(event)) event.preventDefault();
        };
        const handlePaste = (event) => {
            if (!takes(event)) return;
            const text = event.clipboardData?.getData('text/plain') ?? '';
            if (!text) return;
            event.preventDefault();
            latestRef.current.onPaste(text);
        };
        const doc = node.ownerDocument;
        doc.addEventListener('beforepaste', handleBeforePaste);
        doc.addEventListener('paste', handlePaste);
        detachRef.current = () => {
            doc.removeEventListener('beforepaste', handleBeforePaste);
            doc.removeEventListener('paste', handlePaste);
        };
    }, []);
}

const formSx = { display: 'flex', flexDirection: 'column', minHeight: 0 };

// Mounted per opening, so the box starts empty.
function PasteFromExcelForm({ titleId, destination, onImport, onClose }) {
    const [text, setText] = useState('');
    const [problem, setProblem] = useState('');
    // A sheet goes to the import and closes the dialog; anything else stays
    // in the box with what is missing.
    const importText = (value) => {
        if (onImport(parseTsv(value))) {
            onClose();
            return true;
        }
        setProblem(NOT_A_SHEET_MESSAGES[destination]);
        return false;
    };

    return (
        <Box
            component="form"
            noValidate
            sx={formSx}
            onSubmit={(event) => {
                event.preventDefault();
                importText(text);
            }}
        >
            <DialogTitle id={titleId}>Paste from Excel</DialogTitle>
            <DialogContent sx={{ display: 'grid', gap: 1.5, pt: '12px !important' }}>
                <Typography variant="body2" color="text.secondary">
                    {`Copy your sheet in Excel, including the header row, and ${PASTE_ACTION} here.`}
                </Typography>
                <TextField
                    autoFocus
                    multiline
                    minRows={6}
                    maxRows={12}
                    value={text}
                    placeholder="Paste your sheet here"
                    inputProps={{ 'aria-label': 'Sheet to import', spellCheck: false }}
                    sx={{ '& textarea': { fontFamily: 'monospace', fontSize: 12 } }}
                    onChange={(event) => {
                        setText(event.target.value);
                        setProblem('');
                    }}
                    onPaste={(event) => {
                        const pasted = event.clipboardData?.getData('text/plain') ?? '';
                        if (!pasted) return;
                        // Text that is not a sheet goes where the browser would put it.
                        event.preventDefault();
                        const box = event.target;
                        const next = `${box.value.slice(0, box.selectionStart)}${pasted}${box.value.slice(box.selectionEnd)}`;
                        if (!importText(pasted)) setText(next);
                    }}
                />
                {problem && (
                    <Alert severity="warning" sx={{ py: 0.25 }}>
                        {problem}
                    </Alert>
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={() => onClose()}>Cancel</Button>
                <Button type="submit" variant="contained" disabled={!text.trim()}>
                    Import
                </Button>
            </DialogActions>
        </Box>
    );
}

/**
 * Paste from Excel: a box to paste a copied sheet into, with the keyboard or
 * a long-press on a touch device. onImport(rows) receives the pasted rows
 * and returns true when it took them as a sheet; the dialog then closes.
 * Other text stays in the box with what the destination's import
 * ('watchlist' or 'portfolio') needs, and Import tries the box's text again
 * after an edit.
 */
export function PasteFromExcelDialog({ open, destination, onImport, onClose }) {
    const titleId = useId();
    return (
        <Dialog open={open} onClose={() => onClose()} maxWidth="sm" fullWidth aria-labelledby={titleId}>
            <PasteFromExcelForm
                titleId={titleId}
                destination={destination}
                onImport={onImport}
                onClose={onClose}
            />
        </Dialog>
    );
}

const DESTINATION_PROP_TYPE = PropTypes.oneOf(Object.keys(NOT_A_SHEET_MESSAGES));

PasteFromExcelForm.propTypes = {
    titleId: PropTypes.string.isRequired,
    destination: DESTINATION_PROP_TYPE.isRequired,
    onImport: PropTypes.func.isRequired,
    onClose: PropTypes.func.isRequired,
};

PasteFromExcelDialog.propTypes = {
    open: PropTypes.bool.isRequired,
    destination: DESTINATION_PROP_TYPE.isRequired,
    onImport: PropTypes.func.isRequired,
    onClose: PropTypes.func.isRequired,
};
